import React, { useEffect, useState } from "react";
import { NavLink, useLocation } from "react-router-dom";
import { getAuthUser, logout } from "./auth";
import "./AdminLayout.css";
import desktopLogo from "./assets/deskpos-logo-desktop.png";
import mobileLogo from "./assets/deskpos-logo-mobile.png";

function AdminLayout({ children }) {
  const location = useLocation();


  const isProductsActive =
  location.pathname === "/inventory" ||
  location.pathname === "/labels";

  const isReportsActive =
    location.pathname === "/reports" ||
    location.pathname === "/sales" ||
    location.pathname === "/profit-report" ||
    location.pathname === "/refund-report";

  const isPurchasesActive =
    location.pathname === "/purchases" ||
    location.pathname === "/suppliers" ||
    location.pathname === "/purchase-categories";

  const isMarketingActive =
  location.pathname === "/promotions" ||
  location.pathname === "/multi-buy-promotions";

  const isShopSettingsActive =
    location.pathname === "/shop-settings" ||
    location.pathname === "/licence" ||
    location.pathname === "/staff";


  const [productsOpen, setProductsOpen] =
  useState(isProductsActive);

  const [reportsOpen, setReportsOpen] =
    useState(isReportsActive);

  const [purchasesOpen, setPurchasesOpen] =
    useState(isPurchasesActive);

  const [marketingOpen, setMarketingOpen] =
    useState(isMarketingActive);

  const [shopSettingsOpen, setShopSettingsOpen] =
    useState(isShopSettingsActive);

  const [mobileMenuOpen, setMobileMenuOpen] =
    useState(false);


  useEffect(() => {
  setProductsOpen(isProductsActive);
  }, [isProductsActive]);

  useEffect(() => {
    setReportsOpen(isReportsActive);
  }, [isReportsActive]);

  useEffect(() => {
    setPurchasesOpen(isPurchasesActive);
  }, [isPurchasesActive]);

  useEffect(() => {
    setMarketingOpen(isMarketingActive);
  }, [isMarketingActive]);

  useEffect(() => {
    setShopSettingsOpen(isShopSettingsActive);
  }, [isShopSettingsActive]);

  useEffect(() => {
    window.scrollTo({
      top: 0,
      left: 0,
      behavior: "auto",
    });
  }, [location.pathname]);
  


  useEffect(() => {
    const resetScroll = () => {
      window.scrollTo(0, 0);
      document.documentElement.scrollTop = 0;
      document.body.scrollTop = 0;
  
    };

    resetScroll();

    const timer = setTimeout(resetScroll, 100);

    return () => clearTimeout(timer);
  }, [location.pathname]);

  useEffect(() => {
  const IDLE_TIMEOUT =
    10 * 60 * 1000; // 10 minutes

  let idleTimer = null;

  const logoutForInactivity = () => {
    console.log(
      "Admin logged out due to inactivity"
    );

    logout();

    window.location.hash =
      "/admin-login";
  };

  const resetIdleTimer = () => {
    if (idleTimer) {
      clearTimeout(idleTimer);
    }

    idleTimer = setTimeout(
      logoutForInactivity,
      IDLE_TIMEOUT
    );
  };

  const activityEvents = [
    "mousedown",
    "mousemove",
    "keydown",
    "touchstart",
    "scroll",
  ];

  activityEvents.forEach(
    (eventName) => {
      window.addEventListener(
        eventName,
        resetIdleTimer,
        {
          passive: true,
        }
      );
    }
  );

  // Start timer immediately
  resetIdleTimer();

  return () => {
    if (idleTimer) {
      clearTimeout(idleTimer);
    }

    activityEvents.forEach(
      (eventName) => {
        window.removeEventListener(
          eventName,
          resetIdleTimer
        );
      }
    );
  };
}, []);


  const user = getAuthUser();

  const closeMobileMenu = () => {
    setMobileMenuOpen(false);
  };

  const handleLogout = () => {
    closeMobileMenu();
    logout();
    window.location.hash = "/admin-login";
  };

 const handleNormalLinkClick = () => {
  setProductsOpen(false);
  setReportsOpen(false);
  setPurchasesOpen(false);
  setMarketingOpen(false);
  setShopSettingsOpen(false);
  closeMobileMenu();
};

const handleProductLinkClick = () => {
  closeMobileMenu();
};

  const handleReportLinkClick = () => {
    closeMobileMenu();
  };

  const handlePurchaseLinkClick = () => {
    closeMobileMenu();
  };

  const handleMarketingLinkClick = () => {
    closeMobileMenu();
  };

  const handleShopSettingsLinkClick = () => {
    closeMobileMenu();
  };

  const isSuperAdmin = user?.role === "super_admin";
  const isShopAdmin = user?.role === "shop_admin";
  const isManager = user?.role === "manager";

  return (
    <div className="admin-layout">
      <aside className={`admin-sidebar ${mobileMenuOpen ? "mobile-open" : ""}`}>
        <div className="sidebar-brand">
          <NavLink
            to="/dashboard"
            className="admin-brand-link"
            onClick={() => setMobileMenuOpen(false)}
          >
            <picture className="admin-brand-logo">
              <source media="(max-width: 768px)" srcSet={mobileLogo} />

              <img src={desktopLogo} alt="DeskPOS" />
            </picture>
          </NavLink>

          <button
            type="button"
            className="admin-sidebar-menu-btn"
            onClick={() => setMobileMenuOpen((previous) => !previous)}
          >
            ☰ Menu
          </button>
        </div>

        <nav className="sidebar-menu">
          {isSuperAdmin && (
            <>
              <NavLink to="/super-admin" end onClick={closeMobileMenu}>
                Manage Shops
              </NavLink>

              <NavLink to="/super-admin/sales" onClick={closeMobileMenu}>
                Manage Sales
              </NavLink>

              <NavLink to="/super-admin/tills" onClick={closeMobileMenu}>
                Manage Tills
              </NavLink>
              <NavLink to="/super-admin/billing" onClick={closeMobileMenu}>
                Billing
              </NavLink>
            </>
          )}

          {(isShopAdmin || isManager) && (
            <>
              <NavLink to="/dashboard" onClick={handleNormalLinkClick}>
                Dashboard
              </NavLink>

              <div
                className={`sidebar-dropdown ${
                  isProductsActive ? "active" : ""
                }`}
              >
                <button
                  type="button"
                  className="sidebar-dropdown-btn"
                  onClick={() => setProductsOpen((previous) => !previous)}
                >
                  <span>Products</span>

                  <span>{productsOpen ? "▾" : "▸"}</span>
                </button>

                {productsOpen && (
                  <div className="sidebar-submenu">
                    <NavLink to="/inventory" onClick={handleProductLinkClick}>
                      Products
                    </NavLink>

                    <NavLink to="/labels" onClick={handleProductLinkClick}>
                      Labels
                    </NavLink>
                  </div>
                )}
              </div>

              <div
                className={`sidebar-dropdown ${
                  isPurchasesActive ? "active" : ""
                }`}
              >
                <button
                  type="button"
                  className="sidebar-dropdown-btn"
                  onClick={() => setPurchasesOpen((previous) => !previous)}
                >
                  <span>Purchases</span>

                  <span>{purchasesOpen ? "▾" : "▸"}</span>
                </button>

                {purchasesOpen && (
                  <div className="sidebar-submenu">
                    <NavLink to="/purchases" onClick={handlePurchaseLinkClick}>
                      Purchases
                    </NavLink>

                    <NavLink to="/suppliers" onClick={handlePurchaseLinkClick}>
                      Suppliers
                    </NavLink>

                    <NavLink
                      to="/purchase-categories"
                      onClick={handlePurchaseLinkClick}
                    >
                      Purchase Categories
                    </NavLink>
                  </div>
                )}
              </div>

              <div
                className={`sidebar-dropdown ${
                  isReportsActive ? "active" : ""
                }`}
              >
                <button
                  type="button"
                  className="sidebar-dropdown-btn"
                  onClick={() => setReportsOpen((previous) => !previous)}
                >
                  <span>Reports</span>

                  <span>{reportsOpen ? "▾" : "▸"}</span>
                </button>

                {reportsOpen && (
                  <div className="sidebar-submenu">
                    <NavLink to="/reports" onClick={handleReportLinkClick}>
                      Accounting Report
                    </NavLink>

                    <NavLink to="/sales" onClick={handleReportLinkClick}>
                      Sale Report
                    </NavLink>

                    <NavLink
                      to="/profit-report"
                      onClick={handleReportLinkClick}
                    >
                      Profit Report
                    </NavLink>

                    <NavLink
                      to="/refund-report"
                      onClick={handleReportLinkClick}
                    >
                      Refund Report
                    </NavLink>
                  </div>
                )}
              </div>

              <div
                className={`sidebar-dropdown ${
                  isMarketingActive ? "active" : ""
                }`}
              >
                <button
                  type="button"
                  className="sidebar-dropdown-btn"
                  onClick={() => setMarketingOpen((previous) => !previous)}
                >
                  <span>Marketing</span>

                  <span>{marketingOpen ? "▾" : "▸"}</span>
                </button>

                {marketingOpen && (
                  <div className="sidebar-submenu">
                    <NavLink
                      to="/promotions"
                      onClick={handleMarketingLinkClick}
                    >
                      Promotions
                    </NavLink>

                    <NavLink
                      to="/multi-buy-promotions"
                      onClick={handleMarketingLinkClick}
                    >
                      Multi-Buy Promotions
                    </NavLink>
                  </div>
                )}
              </div>

              <div
                className={`sidebar-dropdown ${
                  isShopSettingsActive ? "active" : ""
                }`}
              >
                <button
                  type="button"
                  className="sidebar-dropdown-btn"
                  onClick={() => setShopSettingsOpen((previous) => !previous)}
                >
                  <span>Shop Settings</span>

                  <span>{shopSettingsOpen ? "▾" : "▸"}</span>
                </button>

                {shopSettingsOpen && (
                  <div className="sidebar-submenu">
                    {isShopAdmin && (
                      <NavLink
                        to="/shop-settings"
                        onClick={handleShopSettingsLinkClick}
                      >
                        Shop Settings
                      </NavLink>
                    )}

                    <NavLink
                      to="/licence"
                      onClick={handleShopSettingsLinkClick}
                    >
                      Licence
                    </NavLink>

                    {isShopAdmin && (
                      <NavLink
                        to="/staff"
                        onClick={handleShopSettingsLinkClick}
                      >
                        Staff
                      </NavLink>
                    )}
                  </div>
                )}
              </div>
            </>
          )}

          {false && (isShopAdmin || isManager) && (
            <NavLink to="/" onClick={handleNormalLinkClick}>
              Open POS
            </NavLink>
          )}

          <button
            type="button"
            className="sidebar-mobile-logout-btn"
            onClick={handleLogout}
          >
            Logout
          </button>
        </nav>

        <div className="sidebar-footer">
          <strong>{user?.name || "User"}</strong>
          <span>{user?.role || ""}</span>

          <button type="button" className="logout-btn" onClick={handleLogout}>
            Logout
          </button>
        </div>
      </aside>

      <main className="admin-main">{children}</main>
    </div>
  );
}

export default AdminLayout;